import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  ActivePrimaryNavigationDestinationNameContext,
  PendingPrimaryNavigationDestinationNameContext,
  PressedPrimaryNavigationDestinationStoreContext,
} from "@-/dev.improve.haniverse.routes.nav._context";
import { View } from "@-/com.npmjs.react-native";
import {
  cancelAnimation,
  createAnimatedComponent,
  Easing,
  type SharedValue,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { useContext, useEffect, useSyncExternalStore } from "react";

const AnimatedView = createAnimatedComponent(View);

/** Resting scale of an unselected tab icon. */
const INACTIVE_SCALE = 1;
/** The settled active tab reads "slightly bigger than the rest". */
const ACTIVE_SCALE = 1.12;
/** Held down, or tapped and still loading: the icon tucks in to acknowledge the tap. */
const PRESSED_SCALE = 0.86;

/**
 * Shimmer while the tapped tab loads. At tab-icon size (a ~22px glyph) a travelling gradient band
 * reads as flicker rather than shine, so the shimmer is a luminance breath on the glyph itself,
 * paired with a matching micro-pulse in scale so it shimmers rather than merely dims.
 */
const SHIMMER_MIN_OPACITY = 0.45;
const SHIMMER_MAX_OPACITY = 1;
const SHIMMER_HALF_CYCLE_MS = 520;
/** Scale added at the shimmer's trough, on top of the pressed tuck. */
const SHIMMER_SCALE_LIFT = 0.04;

/**
 * Settling is the moment the tab "lands", so it gets a spring with a touch of overshoot rather
 * than a timing curve — the icon pops out of the pressed tuck into its active size.
 */
const SETTLE_SPRING = { damping: 12, mass: 0.5, stiffness: 240 } as const;
/** Tucking in under the finger is a short, non-bouncy contraction. */
const PRESS_TIMING = { duration: 140, easing: Easing.out(Easing.quad) } as const;

export function AnimatedPrimaryNavigationStripIcon({
  inactiveSize,
  name,
  revealProgress,
  tabName,
}: {
  inactiveSize: number;
  name: IconName;
  /**
   * 0 → 1 as the icon first pops into the strip (a destination the onboarding has just
   * unlocked); left out, the icon is simply there.
   */
  revealProgress?: SharedValue<number>;
  tabName: string;
}) {
  // React Navigation renders each tab icon twice: a focused-layer copy and an
  // unfocused-layer copy that it cross-fades. Its per-copy `focused` and `color` props
  // therefore do not describe the tab's actual state. Derive the COMPLETE presentation
  // from the authoritative active tab instead, so both copies are identical and a stale
  // layer can never combine the active color with the inactive color.
  const activePrimaryNavigationDestinationName = useContext(
    ActivePrimaryNavigationDestinationNameContext,
  );
  const pendingPrimaryNavigationDestinationName = useContext(
    PendingPrimaryNavigationDestinationNameContext,
  );
  const isActive = activePrimaryNavigationDestinationName === tabName;
  // Tapped but not yet landed. The bar promotes the tapped tab optimistically, so this tab is
  // already painted active; pending is purely the "still loading" state layered on top.
  const isPending = pendingPrimaryNavigationDestinationName === tabName;
  const iconClassName = isActive ? "text-primary dark:text-foreground" : "text-muted-foreground";

  // Held under the finger. Subscribed per-icon via an external store so a press re-renders only
  // the pressed icon — never the navigator or the other four tabs.
  const pressedTabStore = useContext(PressedPrimaryNavigationDestinationStoreContext);
  const pressedTabName = useSyncExternalStore(
    pressedTabStore?.subscribe ?? subscribeToNothing,
    pressedTabStore?.getSnapshot ?? getNoPressedTab,
    pressedTabStore?.getSnapshot ?? getNoPressedTab,
  );
  const isPressed = pressedTabName === tabName;

  // The tuck covers the whole gesture: it starts under the finger and, when the destination is
  // slow, simply stays put through the pending window instead of bouncing back and shrinking
  // again. A tab whose scene is already mounted therefore still reads as pressed, because the
  // press — not the navigation — is what starts the animation.
  const isTucked = isPressed || isPending;

  const reduceMotion = useReducedMotion();
  const scale = useSharedValue(isActive ? ACTIVE_SCALE : INACTIVE_SCALE);
  const shimmer = useSharedValue(SHIMMER_MAX_OPACITY);

  useEffect(() => {
    const target = isTucked ? PRESSED_SCALE : isActive ? ACTIVE_SCALE : INACTIVE_SCALE;
    if (reduceMotion) {
      scale.set(target);
      return;
    }
    // Contracting under the finger is a plain ease-out; landing springs so the icon visibly
    // pops back out to its active size.
    scale.set(isTucked ? withTiming(target, PRESS_TIMING) : withSpring(target, SETTLE_SPRING));
  }, [isActive, isTucked, reduceMotion, scale]);

  useEffect(() => {
    // The shimmer means "loading", so it is driven by the pending window alone — a press on an
    // instant tab tucks and springs back without ever shimmering.
    if (!isPending || reduceMotion) {
      // Ride whatever opacity the shimmer was at back to solid instead of snapping, so a fast
      // tab switch doesn't end on a visible step.
      cancelAnimation(shimmer);
      shimmer.set(withTiming(SHIMMER_MAX_OPACITY, PRESS_TIMING));
      return;
    }
    shimmer.set(
      withRepeat(
        withSequence(
          withTiming(SHIMMER_MIN_OPACITY, {
            duration: SHIMMER_HALF_CYCLE_MS,
            easing: Easing.inOut(Easing.quad),
          }),
          withTiming(SHIMMER_MAX_OPACITY, {
            duration: SHIMMER_HALF_CYCLE_MS,
            easing: Easing.inOut(Easing.quad),
          }),
        ),
        -1,
      ),
    );
    return () => cancelAnimation(shimmer);
  }, [isPending, reduceMotion, shimmer]);

  const animatedStyle = useAnimatedStyle(() => {
    // Ride the shimmer into scale as well so the pulse has depth: the glyph swells slightly as
    // it dims. `shimmer` rests at its MAX whenever nothing is loading, so the resting lift is
    // exactly zero and the settled active size is untouched.
    const dip = (SHIMMER_MAX_OPACITY - shimmer.get()) / (SHIMMER_MAX_OPACITY - SHIMMER_MIN_OPACITY);
    const reveal = revealProgress?.get() ?? 1;
    return {
      opacity: shimmer.get() * Math.min(1, Math.max(0, reveal)),
      transform: [{ scale: (scale.get() + dip * SHIMMER_SCALE_LIFT) * Math.max(0, reveal) }],
    };
  });

  return (
    <AnimatedView className="size-7.5 items-center justify-center" style={animatedStyle}>
      <Icon className={iconClassName} name={name} size={inactiveSize} />
    </AnimatedView>
  );
}

/** Stable no-op subscription for icons rendered outside the tab bar (previews, tests, drawer). */
function subscribeToNothing() {
  return () => {};
}
function getNoPressedTab() {
  return null;
}
